import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  UserCog,
  Plus,
  CheckCircle2,
  Trash2,
  Edit,
  X,
  Shield,
  Building2,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const DEPARTMENTS = [
  { id: 'finance', name: 'Finance', icon: '💰', color: 'emerald' },
  { id: 'choir', name: 'Choir', icon: '🎵', color: 'purple' },
  { id: 'shepherd', name: "Shepherd's Corner", icon: '🙏', color: 'indigo' },
  { id: 'projects', name: 'Church Projects', icon: '🌾', color: 'amber' },
  { id: 'email', name: 'Email', icon: '📧', color: 'blue' },
  { id: 'usher', name: 'Usher', icon: '🚪', color: 'orange' },
  { id: 'sundayschool', name: 'Sunday School', icon: '📚', color: 'green' },
  { id: 'media', name: 'Media', icon: '📺', color: 'red' },
  { id: 'secretary', name: 'Secretary', icon: '📝', color: 'slate' },
  { id: 'announcements', name: 'Announcements', icon: '📢', color: 'cyan' },
  { id: 'analytics', name: 'Analytics', icon: '📊', color: 'violet' },
  { id: 'rota', name: 'Church Rota', icon: '📅', color: 'teal' },
  { id: 'rules', name: 'Rules & Tenets', icon: '📜', color: 'rose' },
  { id: 'biblelessons', name: 'Bible Lessons', icon: '📖', color: 'indigo' },
  { id: 'volunteer', name: 'Volunteer Attendance', icon: '🤝', color: 'amber' }
];

const ROLES = [
  { value: 'main_admin', label: 'Main Admin', color: 'rose', desc: 'Full access to all modules', icon: '👑' },
  { value: 'shepherd', label: 'Shepherd', color: 'purple', desc: 'Can send messages to all departments', icon: '🙏' },
  { value: 'sub_admin', label: 'Sub Admin', color: 'blue', desc: 'Custom department access', icon: '👤' }
];

const AdminUsersSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'sub_admin',
    permissions: []
  });
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [deactivatedUsers, setDeactivatedUsers] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.filter(u => u.is_active !== false));
      setDeactivatedUsers(res.data.filter(u => u.is_active === false));
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      role: 'sub_admin',
      permissions: []
    });
    setEditingUser(null);
    setShowAddForm(false);
    setShowPassword(false);
  };

  const handleSubmit = async () => {
    if (!formData.username || !formData.full_name) {
      alert('Please fill in username and full name');
      return;
    }
    if (!editingUser && !formData.password) {
      alert('Please enter a password');
      return;
    }
    
    try {
      if (editingUser) {
        // Update user
        const updateData = {
          ...formData,
          password: formData.password || editingUser.password_hash // Keep existing if not changed
        };
        await axios.put(`${API}/admin/users/${editingUser.id}`, updateData);
      } else {
        // Create user
        await axios.post(`${API}/admin/users`, formData);
      }
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorData = error.response?.data;
      let errorMsg = 'Error saving user';
      if (typeof errorData?.detail === 'string') {
        errorMsg = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        // Pydantic validation errors
        errorMsg = errorData.detail.map(e => e.msg || e.message).join(', ');
      } else if (errorData?.message) {
        errorMsg = errorData.message;
      }
      alert(errorMsg);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name,
      email: user.email || '',
      role: user.role,
      permissions: user.permissions || []
    });
    setEditingUser(user);
    setShowAddForm(true);
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      loadData();
    } catch (error) {
      const errorData = error.response?.data;
      let errorMsg = 'Error deactivating user';
      if (typeof errorData?.detail === 'string') {
        errorMsg = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        errorMsg = errorData.detail.map(e => e.msg || e.message).join(', ');
      }
      alert(errorMsg);
    }
  };

  const handleReactivate = async (userId) => {
    if (!window.confirm('Are you sure you want to reactivate this user?')) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/reactivate`);
      loadData();
    } catch (error) {
      const errorData = error.response?.data;
      let errorMsg = 'Error reactivating user';
      if (typeof errorData?.detail === 'string') {
        errorMsg = errorData.detail;
      }
      alert(errorMsg);
    }
  };

  const togglePermission = (deptId) => {
    setFormData(prev => {
      const current = prev.permissions || [];
      if (current.includes(deptId)) {
        return { ...prev, permissions: current.filter(p => p !== deptId) };
      } else {
        return { ...prev, permissions: [...current, deptId] };
      }
    });
  };

  const handleRoleChange = (role) => {
    let permissions = [];
    if (role === 'main_admin') {
      permissions = ['finance', 'media', 'secretary', 'choir', 'usher', 'shepherd', 'main_admin'];
    } else if (role === 'shepherd') {
      permissions = ['shepherd'];
    }
    setFormData(prev => ({ ...prev, role, permissions }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-3xl p-6 text-white shadow-xl"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white/20 backdrop-blur-sm">
              <UserCog className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Admin Users</h2>
              <p className="text-cyan-100">Manage sub-admin accounts and department access</p>
            </div>
          </div>
          <motion.button
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-cyan-600 font-bold hover:bg-cyan-50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="w-5 h-5" />
            Create Sub-Admin
          </motion.button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{users.filter(u => u.is_active !== false).length}</p>
            <p className="text-sm text-cyan-200">Active Users</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{users.filter(u => u.role === 'main_admin').length}</p>
            <p className="text-sm text-cyan-200">Main Admins</p>
          </div>
          <div className="bg-white/20 rounded-xl p-4 text-center backdrop-blur-sm">
            <p className="text-3xl font-bold">{users.filter(u => u.role === 'sub_admin' || u.role === 'shepherd').length}</p>
            <p className="text-sm text-cyan-200">Sub Admins</p>
          </div>
        </div>
      </motion.div>

      {/* Role Legend */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-slate-600" />
          Role Types
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          {ROLES.map(role => (
            <div key={role.value} className={`p-4 rounded-xl bg-${role.color}-50 border border-${role.color}-200`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">{role.icon}</span>
                <p className={`font-bold text-${role.color}-700`}>{role.label}</p>
              </div>
              <p className="text-sm text-slate-600">{role.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => resetForm()}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500">
                      <UserCog className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800">
                      {editingUser ? 'Edit Admin User' : 'Create Sub-Admin'}
                    </h3>
                  </div>
                  <button
                    onClick={() => resetForm()}
                    className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-6 h-6 text-slate-600" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-150px)]">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Username *</label>
                    <input
                      type="text"
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-cyan-400 focus:outline-none transition-colors"
                      placeholder="Username"
                      value={formData.username}
                      onChange={e => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      disabled={editingUser?.role === 'main_admin'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Password {editingUser ? '(leave blank to keep)' : '*'}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 pr-12 focus:border-cyan-400 focus:outline-none transition-colors"
                        placeholder={editingUser ? '••••••••' : 'Password'}
                        value={formData.password}
                        onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-cyan-400 focus:outline-none transition-colors"
                      placeholder="Full Name"
                      value={formData.full_name}
                      onChange={e => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                    <input
                      type="email"
                      className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 focus:border-cyan-400 focus:outline-none transition-colors"
                      placeholder="Email (optional)"
                      value={formData.email}
                      onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    <Shield className="w-4 h-4 inline mr-2" />
                    Role *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {ROLES.map(role => (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => handleRoleChange(role.value)}
                        disabled={editingUser?.role === 'main_admin' && role.value !== 'main_admin'}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          formData.role === role.value
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-slate-200 hover:border-slate-300'
                        } ${editingUser?.role === 'main_admin' && role.value !== 'main_admin' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className="text-xl">{role.icon}</span>
                        <p className="font-semibold text-slate-800 mt-1">{role.label}</p>
                        <p className="text-xs text-slate-500">{role.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Department Permissions */}
                {formData.role === 'sub_admin' && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      <Building2 className="w-4 h-4 inline mr-2" />
                      Department Access
                    </label>
                    <p className="text-xs text-slate-500 mb-3">Select which departments this user can access</p>
                    <div className="grid grid-cols-2 gap-3">
                      {DEPARTMENTS.map(dept => (
                        <button
                          key={dept.id}
                          type="button"
                          onClick={() => togglePermission(dept.id)}
                          className={`p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                            formData.permissions?.includes(dept.id)
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <span className="text-xl">{dept.icon}</span>
                          <div className="text-left">
                            <p className="font-medium text-slate-800">{dept.name}</p>
                            {formData.permissions?.includes(dept.id) && (
                              <p className="text-xs text-emerald-600">✓ Has access</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Info box for role */}
                {formData.role === 'main_admin' && (
                  <div className="p-4 rounded-xl bg-rose-50 border border-rose-200">
                    <p className="text-sm text-rose-700">
                      <strong>Main Admin</strong> has full access to all modules including Settings, Audit Logs, and User Management.
                    </p>
                  </div>
                )}

                {formData.role === 'shepherd' && (
                  <div className="p-4 rounded-xl bg-purple-50 border border-purple-200">
                    <p className="text-sm text-purple-700">
                      <strong>Shepherd</strong> can send messages to any department through Shepherd's Corner but cannot access other admin modules.
                    </p>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => resetForm()}
                    className="flex-1 py-3 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <motion.button
                    onClick={handleSubmit}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <CheckCircle2 className="w-5 h-5" />
                    {editingUser ? 'Update User' : 'Create User'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Users List */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4">
          Active Users ({users.filter(u => u.is_active !== false).length})
        </h3>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading users...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {users.filter(u => u.is_active !== false).map((user, index) => {
              const roleInfo = ROLES.find(r => r.value === user.role) || ROLES[2];
              const userPermissions = user.permissions || [];
              
              return (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-cyan-50 border border-slate-200 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                        {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-800">{user.full_name}</p>
                          <span className="text-lg">{roleInfo.icon}</span>
                        </div>
                        <p className="text-sm text-slate-600">@{user.username} • {roleInfo.label}</p>
                        {user.email && <p className="text-xs text-slate-500">{user.email}</p>}
                        
                        {/* Department badges */}
                        {user.role === 'sub_admin' && userPermissions.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {userPermissions.filter(p => !['main_admin', 'shepherd'].includes(p)).map(perm => {
                              const dept = DEPARTMENTS.find(d => d.id === perm);
                              return dept ? (
                                <span key={perm} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">
                                  {dept.icon} {dept.name}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-3 py-1 rounded-full bg-${roleInfo.color}-100 text-${roleInfo.color}-700 text-xs font-medium`}>
                        {roleInfo.label}
                      </span>
                      <motion.button
                        onClick={() => handleEdit(user)}
                        className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Edit User"
                      >
                        <Edit className="w-4 h-4" />
                      </motion.button>
                      {user.role !== 'main_admin' && (
                        <motion.button
                          onClick={() => handleDeactivate(user.id)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="Deactivate User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </motion.button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Deactivated Users Section */}
      {deactivatedUsers.length > 0 && (
        <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">
              Deactivated Users ({deactivatedUsers.length})
            </h3>
            <button
              onClick={() => setShowDeactivated(!showDeactivated)}
              className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 transition-colors"
            >
              {showDeactivated ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {showDeactivated && (
            <div className="space-y-3">
              {deactivatedUsers.map((user, index) => {
                const roleInfo = ROLES.find(r => r.value === user.role) || ROLES[2];
                
                return (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 rounded-2xl bg-gradient-to-r from-slate-100 to-red-50 border border-slate-200 opacity-75"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-600">{user.full_name}</p>
                            <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs">Deactivated</span>
                          </div>
                          <p className="text-sm text-slate-500">@{user.username} • {roleInfo.label}</p>
                        </div>
                      </div>
                      
                      <motion.button
                        onClick={() => handleReactivate(user.id)}
                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium hover:shadow-lg transition-all flex items-center gap-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <RefreshCw className="w-4 h-4" />
                        Reactivate
                      </motion.button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminUsersSection;
